import { useState, useEffect, useMemo } from "react";
import { useAppSelector } from "../../../../../store/types";
import SbuxSelect from "../../../../../components/SbuxSelect";
import SbuxTextField from "../../../../../components/SubxTextField";
import SbuxSubTabs from "../../../../../components/SbuxTabs/SbuxSubTabs";
import ClosureMsgTimePicker2 from "./components/ClosureMsgTimePicker2";
import ClosureMsgDailyFrequency from "./components/ClosureMsgDailyFrequency";
import ClosureMsgWeeklyFrequency from "./components/ClosureMessageWeeklyFrequency";
import ClosureMsgMonthlyFrequency from "./components/ClosureMsgMonthlyFrequency";
import ClosureMsgRangeRecurrence from "./components/ClosureMsgRangeRecurrence";
import ClosureMsgYearlyFrequency from "./components/ClosureMsgYearlyFrequency";
import ClosureAudioConfig from "../../common/ClosureAudioConfig";
import SbuxRadioGroup from "../../../../../components/SbuxRadioGroup";
import useTextTranslate from "../../../hooks/useTextTranslate";
import { Box, Divider, InputLabel, FormControl } from "@mui/material";
import dayjs from "../../../../../utils/dayjs";
import {
  closureRepeatEndDate,
  dayjsToISOString,
  type RepeatPattern,
} from "../../utils";
import { CM_RECURRENCE_FREQUENCY_LIST } from "../../../../../constants";
import styles from "../../styles";
import useCss from "../../../../../hooks/useCss";
import { getMediaMgtDataSelector } from "../../../../../selectors/mediaMgtSelector";
import SbuxTranslate from "../../../../../components/SbuxTranslate";
import {
  MediaItem,
  MediaMgtDataContainer,
} from "../../../../../slices/mediaMgtSlice";
import type { Dayjs } from "dayjs";

interface Language {
  code: string;
  name: string;
}

interface GlobalConfigUiItem {
  value: string;
  name: string;
  [key: string]: unknown;
}

interface GlobalConfigUiClosure {
  series: GlobalConfigUiItem[];
  category: GlobalConfigUiItem[];
  monthly: GlobalConfigUiItem[];
}

interface GlobalConfigUi {
  timezones: GlobalConfigUiItem[];
  closure: GlobalConfigUiClosure;
  weekdays: GlobalConfigUiItem[];
  months: GlobalConfigUiItem[];
}

interface BusinessItem {
  value: string;
  name: string;
  [key: string]: unknown;
}

interface LanguageMessageEntry {
  type: string;
  value: string;
  accessType: string;
}

interface LanguageMessage {
  [key: string]: LanguageMessageEntry;
}

interface RowData {
  hoursCode?: string;
  groupName?: string;
  category?: string;
  type?: string;
  startDate?: Date;
  endDate?: Date;
  startTime?: string;
  endTime?: string;
  startTimeZone?: string;
  endTimeZone?: string;
  description?: string;
  repeatPattern?: RepeatPattern;
  isDailyOption2Selected?: boolean;
  isMonthlyOption2Selected?: boolean;
  isYearlyOption2Selected?: boolean;
  isReccuringOption2Selected?: boolean;
  languageMessage?: LanguageMessage;
  [key: string]: unknown;
}

interface Props {
  row: Partial<RowData>;
  business: BusinessItem[];
  languages: Language[];
  globalConfigUi: GlobalConfigUi;
  handleClosureMsgRow: (row: RowData, flag?: boolean) => void;
}

const ClosureMsgAdd = ({
  row,
  business,
  languages,
  globalConfigUi,
  handleClosureMsgRow,
}: Props) => {
  const classes = useCss(styles);

  const mediaMgtData = useAppSelector(getMediaMgtDataSelector);

  const getMediaItems = (): MediaItem[] => {
    if (!mediaMgtData) return [];
    if (Array.isArray(mediaMgtData)) return mediaMgtData as MediaItem[];
    return (mediaMgtData as MediaMgtDataContainer).data ?? [];
  };

  const [description, setDescription] = useState("");
  const [descriptionError, setDescriptionError] = useState(false);
  const [startDate, setStartDate] = useState<Date>(new Date());
  const [endDate, setEndDate] = useState<Date>(new Date());
  const [startTime, setStartTime] = useState<string>("");
  const [endTime, setEndTime] = useState<string>("");
  const [startTimeZone, setStartTimeZone] = useState<string>(
    globalConfigUi.timezones[3]?.value ?? "",
  );
  const [endTimeZone, setEndTimeZone] = useState<string>(
    globalConfigUi.timezones[3]?.value ?? "",
  );

  const [businessId, setBusinessId] = useState<string>(
    business[0]?.value ?? "",
  );

  const [languageTabId, setLanguageTabId] = useState<string>(
    languages[0]?.code ?? "",
  );
  const [languageMessage, setLanguageMessage] = useState<LanguageMessage>({});
  const [categoryId, setCategoryId] = useState<string>("");
  const [seriesSettingsType, setSeriesSettingsType] = useState<string>(
    globalConfigUi.closure.series[0]?.value ?? "",
  );
  const [recPatternOption, setRecPatternOption] = useState<string>(
    CM_RECURRENCE_FREQUENCY_LIST[0]?.value ?? "",
  );
  const descriptionPlaceHolder = useTextTranslate("Description");
  const [endByOption, setEndByOption] = useState<string>("endByEndDate");
  const [endByEndDate, setEndByEndDate] = useState<Dayjs>(
    closureRepeatEndDate(),
  );

  const [timeLable, setTimeLabel] = useState<string>("");
  const [rowData, setRowData] = useState<RowData | null>(null);

  useEffect(() => {
    const remainder = 30 - (dayjs().minute() % 30);
    const st = dayjs().add(remainder, "minutes").format("hh:mm A");
    setStartTime(st);
    const et = dayjs(st, "hh:mm A").add(30, "minutes").format("hh:mm A");
    setEndTime(et);
  }, []);

  useEffect(() => {
    if (timeLable === "Start Time") {
      const endTimeUpdate = dayjs(startTime, "hh:mm A")
        .add(30, "minutes")
        .format("hh:mm A");
      setEndTime(endTimeUpdate);
      const repeatPattern: RepeatPattern = {
        repeatPattern: rowData?.repeatPattern
          ? { ...rowData.repeatPattern }
          : {
              frequency: CM_RECURRENCE_FREQUENCY_LIST[0]?.value,
              interval: 1,
            },
      } as RepeatPattern;
      const addRow: RowData = {
        ...(rowData ?? {}),
        startTime: startTime,
        endTime: endTimeUpdate,
        ...repeatPattern,
        ...languageMessage,
      };
      setRowData(addRow);
      handleClosureMsgRow(addRow, true);
    }
  }, [startTime, endTime]);

  function createInitialObj(): RowData {
    const remainder = 30 - (dayjs().minute() % 30);
    const st = dayjs().add(remainder, "minutes").format("hh:mm A");
    const et = dayjs(st, "hh:mm A").add(30, "minutes").format("hh:mm A");

    return {
      hoursCode: business[0]?.value,
      groupName: business[0]?.name,
      category: "",
      type: globalConfigUi.closure.series[0]?.value,
      startDate: new Date(),
      endDate: new Date(),
      startTime: st,
      endTime: et,
      startTimeZone: globalConfigUi.timezones[3]?.value ?? "",
      endTimeZone: globalConfigUi.timezones[3]?.value ?? "",
      description: "",
    };
  }

  useEffect(() => {
    const initialObj = createInitialObj();
    setRowData({ ...initialObj, ...row });
  }, []);

  useEffect(() => {
    if (seriesSettingsType === "Repeat" && rowData) {
      const repeatPattern: RepeatPattern = {
        ...rowData.repeatPattern,
        until: dayjsToISOString(endByEndDate),
      };
      setRowData({ ...rowData, repeatPattern });
    }
  }, [seriesSettingsType]);

  const handleBusinessChange = (value: string) => {
    setBusinessId(value);
    const addRow: RowData = {
      ...(rowData ?? {}),
      hoursCode: value,
      groupName: business.find((item) => item.value === value)?.name ?? "",
    };
    setRowData(addRow);
    handleClosureMsgRow(addRow, true);
  };

  const handleCategoryChange = (value: string) => {
    setCategoryId(value);
    const addRow: RowData = {
      ...(rowData ?? {}),
      category: value,
    };
    setRowData(addRow);
    handleClosureMsgRow(addRow, true);
  };

  const handleSeriesChange = (value: string) => {
    setSeriesSettingsType(value);
    if (value === "Repeat") {
      const addRow: RowData = {
        ...(rowData ?? {}),
        type: value,
        repeatPattern: {
          frequency: CM_RECURRENCE_FREQUENCY_LIST[0]?.value,
          interval: 1,
        },
      };
      setRowData(addRow);
      handleClosureMsgRow(addRow, true);
    }
  };

  const handleDescriptionChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = event.target.value;
    setDescription(value);
    setDescriptionError(value.length < 1);
    const addRow: RowData = {
      ...(rowData ?? {}),
      description: value,
    };
    setRowData(addRow);
    handleClosureMsgRow(addRow, true);
  };

  const handleDateChange = (value: { name?: string; date?: Date | null }) => {
    let addRow: RowData;
    if (value.name === "Start Time") {
      setStartDate(value.date ?? new Date());
      setEndDate(value.date ?? new Date());
      addRow = {
        ...(rowData ?? {}),
        startDate: value.date ?? undefined,
        endDate: value.date ?? undefined,
      };
    } else {
      setEndDate(value.date ?? new Date());
      addRow = {
        ...(rowData ?? {}),
        endDate: value.date ?? undefined,
      };
    }
    setRowData(addRow);
    handleClosureMsgRow(addRow, true);
  };

  const handleStartTimeChange = (value: { time?: string }) => {
    setStartTime(value.time ?? "");
    setTimeLabel("Start Time");
    const addRow: RowData = {
      ...(rowData ?? {}),
      startTime: value.time,
      ...languageMessage,
    };
    setRowData(addRow);
    handleClosureMsgRow(addRow, true);
  };

  const handleEndTimeChange = (value: { time?: string }) => {
    setEndTime(value.time ?? "");
    setTimeLabel("End Time");
    const addRow: RowData = {
      ...(rowData ?? {}),
      endTime: value.time,
      ...languageMessage,
    };
    setRowData(addRow);
    handleClosureMsgRow(addRow, true);
  };

  const handleTimeZoneChange = (timeZone: string) => {
    setStartTimeZone(timeZone);
    setEndTimeZone(timeZone);
    const addRow: RowData = {
      ...(rowData ?? {}),
      startTimeZone: timeZone,
      endTimeZone: timeZone,
    };
    setRowData(addRow);
    handleClosureMsgRow(addRow, true);
  };

  const handleRecPatternOptioneChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRecPatternOption(event.target.value);
    const addRow: RowData = {
      ...(rowData ?? {}),
      repeatPattern: {
        frequency: event.target.value,
      },
    };
    setRowData(addRow);
    handleClosureMsgRow(addRow, true);
  };

  const [dailyOption, setDailyOption] = useState<string>(
    "dailyOption1EveryDays",
  );
  const [dailyOptoin1InputValue, setDailyOptoin1InputValue] =
    useState<number>(1);

  const handleDailyOptionsValueChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    let addRow: RowData;
    if (event.target.value === "dailyOption2EveryWeekDay") {
      addRow = {
        ...(rowData ?? {}),
        isDailyOption2Selected: true,
        repeatPattern: {
          ...(rowData?.repeatPattern ?? {}),
          byWeekDay: ["MO", "TU", "WE", "TH", "FR"],
          until: dayjsToISOString(endByEndDate),
        },
      };
    } else {
      addRow = {
        ...(rowData ?? {}),
        isDailyOption2Selected: false,
        repeatPattern: {
          ...(rowData?.repeatPattern ?? {}),
          interval: dailyOptoin1InputValue,
          until: dayjsToISOString(endByEndDate),
        },
      };
    }
    setDailyOption(event.target.value);
    setRowData(addRow);
    handleClosureMsgRow(addRow);
  };

  const handleDailyOptoin1InputValue = (value: string | number) => {
    setDailyOptoin1InputValue(Number(value));
    const addRow: RowData = {
      ...(rowData ?? {}),
      repeatPattern: {
        ...(rowData?.repeatPattern ?? {}),
        interval: Number(value),
      },
    };
    setRowData(addRow);
    handleClosureMsgRow(addRow, true);
  };

  const [weeklyRecEveryInputValue, setWeeklyRecEveryInputValue] =
    useState<number>(1);
  const [weeklyRecDaysChecked, setWeeklyRecDaysChecked] = useState<string[]>(
    [],
  );

  const handleWeeklyRecEveryInputValueChange = (value: string | number) => {
    setWeeklyRecEveryInputValue(Number(value));
    const addRow: RowData = {
      ...(rowData ?? {}),
      repeatPattern: {
        ...(rowData?.repeatPattern ?? {}),
        interval: Number(value),
      },
    };
    setRowData(addRow);
    handleClosureMsgRow(addRow, true);
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let data: string[];
    if (event.target.checked) {
      const arr = [...weeklyRecDaysChecked];
      arr.push(event.target.name);
      data = [...arr];
      setWeeklyRecDaysChecked(data);
    } else {
      data = weeklyRecDaysChecked.filter((f) => f !== event.target.name);
      setWeeklyRecDaysChecked(data);
    }
    const addRow: RowData = {
      ...(rowData ?? {}),
      repeatPattern: {
        ...(rowData?.repeatPattern ?? {}),
        byWeekDay: data,
      },
    };
    setRowData(addRow);
    handleClosureMsgRow(addRow, true);
  };

  const [monthlyOption, setMonthlyOption] =
    useState<string>("monthlyOption1Day");
  const [monthlyRecDayOfValue, setMonthlyRecDayOfValue] = useState<string>(
    globalConfigUi.closure.monthly[0]?.value ?? "",
  );
  const [monthlyRecWeekDays, setMonthlyRecWeekDays] = useState<string>(
    globalConfigUi.weekdays[0]?.value ?? "",
  );
  const [
    monthlyRecOption1EveryDayInputValue,
    setMonthlyRecOption1EveryDayInputValue,
  ] = useState<number>(1);
  const [
    monthlyRecOption1EveryMonthsInputValue,
    setMonthlyRecOption1EveryMonthsInputValue,
  ] = useState<number>(1);
  const [
    monthlyRecOption2EveryMonthsInputValue,
    setMonthlyMonthlyRecOption2EveryMonthsInputValue,
  ] = useState<number>(1);

  const handleMonthlyOptionsValueChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    let addRow: RowData;
    if (event.target.value === "monthlyOption2The") {
      addRow = {
        ...(rowData ?? {}),
        isMonthlyOption2Selected: true,
        repeatPattern: {
          ...(rowData?.repeatPattern ?? {}),
          byWeekDay: [monthlyRecWeekDays],
          interval: monthlyRecOption2EveryMonthsInputValue,
          byWeekDayN: monthlyRecDayOfValue,
          until: dayjsToISOString(endByEndDate),
        },
      };
    } else {
      addRow = {
        ...(rowData ?? {}),
        isMonthlyOption2Selected: false,
        repeatPattern: {
          ...(rowData?.repeatPattern ?? {}),
          byDay: monthlyRecOption1EveryDayInputValue,
          interval: monthlyRecOption1EveryMonthsInputValue,
          until: dayjsToISOString(endByEndDate),
        },
      };
    }
    setMonthlyOption(event.target.value);
    setRowData(addRow);
    handleClosureMsgRow(addRow, true);
  };

  const handleMonthlyRecDayOfValueChange = (value: string) => {
    setMonthlyRecDayOfValue(value);
    const addRow: RowData = {
      ...(rowData ?? {}),
      repeatPattern: {
        ...(rowData?.repeatPattern ?? {}),
        byWeekDayN: value,
      },
    };
    setRowData(addRow);
    handleClosureMsgRow(addRow, true);
  };

  const handleMonthlyRecWeekDaysChange = (value: string) => {
    setMonthlyRecWeekDays(value);
    const addRow: RowData = {
      ...(rowData ?? {}),
      repeatPattern: {
        ...(rowData?.repeatPattern ?? {}),
        byWeekDay: [value],
      },
    };
    setRowData(addRow);
    handleClosureMsgRow(addRow, true);
  };

  const handleMonthlyRecOption1EveryDayInputValueChange = (
    value: string | number,
  ) => {
    setMonthlyRecOption1EveryDayInputValue(Number(value));
    const addRow: RowData = {
      ...(rowData ?? {}),
      repeatPattern: {
        ...(rowData?.repeatPattern ?? {}),
        byDay: Number(value),
      },
    };
    setRowData(addRow);
    handleClosureMsgRow(addRow, true);
  };

  const handleMonthlyRecOption1EveryMonthsInputValueChange = (
    value: string | number,
  ) => {
    setMonthlyRecOption1EveryMonthsInputValue(Number(value));
    const addRow: RowData = {
      ...(rowData ?? {}),
      repeatPattern: {
        ...(rowData?.repeatPattern ?? {}),
        interval: Number(value),
      },
    };
    setRowData(addRow);
    handleClosureMsgRow(addRow, true);
  };

  const handleMonthlyRecOption2EveryMonthsInputValueChange = (
    value: string | number,
  ) => {
    const addRow: RowData = {
      ...(rowData ?? {}),
      repeatPattern: {
        ...(rowData?.repeatPattern ?? {}),
        interval: Number(value),
      },
    };
    setMonthlyMonthlyRecOption2EveryMonthsInputValue(Number(value));
    setRowData(addRow);
    handleClosureMsgRow(addRow, true);
  };

  const [yearlyOption, setYearlyOption] = useState<string>("yearlyOption1On");
  const [yearlyRecEveryInputValue, setYearlyRecEveryInputValue] =
    useState<number>(1);

  const handleYearlyRecEveryInputValueChange = (value: string | number) => {
    setYearlyRecEveryInputValue(Number(value));
    const addRow: RowData = {
      ...(rowData ?? {}),
      repeatPattern: {
        ...(rowData?.repeatPattern ?? {}),
        interval: Number(value),
      },
    };
    setRowData(addRow);
    handleClosureMsgRow(addRow, true);
  };

  const [yearlyOption1MonthValue, setYearlyOption1MonthValue] =
    useState<string>(globalConfigUi.months[0]?.value ?? "");

  const handleYearlyOption1MonthValueChange = (value: string) => {
    setYearlyOption1MonthValue(value);
    const addRow: RowData = {
      ...(rowData ?? {}),
      repeatPattern: {
        ...(rowData?.repeatPattern ?? {}),
        byMonth: value,
      },
    };
    setRowData(addRow);
    handleClosureMsgRow(addRow, true);
  };

  const [yearlyOption1OnInputValue, setYearlyOption1OnInputValue] =
    useState<number>(1);

  const handleYearlyOption1OnInputValueChange = (value: string | number) => {
    setYearlyOption1OnInputValue(Number(value));
    const addRow: RowData = {
      ...(rowData ?? {}),
      repeatPattern: {
        ...(rowData?.repeatPattern ?? {}),
        byDay: Number(value),
      },
    };
    setRowData(addRow);
    handleClosureMsgRow(addRow, true);
  };

  const [yearlyRecDayOfValue, setYearlyRecDayOfValue] = useState<string>(
    globalConfigUi.closure.monthly[0]?.value ?? "",
  );
  const [yearlyRecWeekDays, setYearlyRecWeekDays] = useState<string>(
    globalConfigUi.weekdays[0]?.value ?? "",
  );

  const handleYearlyRecDayOfValueChange = (value: string) => {
    setYearlyRecDayOfValue(value);
    const addRow: RowData = {
      ...(rowData ?? {}),
      repeatPattern: {
        ...(rowData?.repeatPattern ?? {}),
        byWeekDayN: value,
      },
    };
    setRowData(addRow);
    handleClosureMsgRow(addRow, true);
  };

  const handleYearlyRecWeekDaysChange = (value: string) => {
    setYearlyRecWeekDays(value);
    const addRow: RowData = {
      ...(rowData ?? {}),
      repeatPattern: {
        ...(rowData?.repeatPattern ?? {}),
        byWeekDay: [value],
      },
    };
    setRowData(addRow);
    handleClosureMsgRow(addRow, true);
  };

  const [yearlyOption2MonthValue, setYearlyOption2MonthValue] =
    useState<string>(globalConfigUi.months[0]?.value ?? "");

  const handleYearlyOption2MonthValueChange = (value: string) => {
    setYearlyOption2MonthValue(value);
    const addRow: RowData = {
      ...(rowData ?? {}),
      repeatPattern: {
        ...(rowData?.repeatPattern ?? {}),
        byMonth: value,
      },
    };
    setRowData(addRow);
    handleClosureMsgRow(addRow, true);
  };

  const handleYearlyOptionsValueChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    let addRow: RowData;
    if (event.target.value === "yearlyOption2OnThe") {
      addRow = {
        ...(rowData ?? {}),
        isYearlyOption2Selected: true,
        repeatPattern: {
          ...(rowData?.repeatPattern ?? {}),
          byWeekDay: [yearlyRecWeekDays],
          interval: yearlyRecEveryInputValue,
          byWeekDayN: yearlyRecDayOfValue,
          byMonth: yearlyOption2MonthValue,
        },
      };
    } else {
      addRow = {
        ...(rowData ?? {}),
        isYearlyOption2Selected: false,
        repeatPattern: {
          ...(rowData?.repeatPattern ?? {}),
          byDay: yearlyOption1OnInputValue,
          byMonth: yearlyOption2MonthValue,
          interval: yearlyRecEveryInputValue,
        },
      };
    }
    setYearlyOption(event.target.value);
    setRowData(addRow);
    handleClosureMsgRow(addRow, true);
  };

  const handleEndByEndDateChange = (value: Date | null) => {
    const dayjsValue = value ? dayjs(value) : dayjs();
    setEndByEndDate(dayjsValue);
    const addRow: RowData = {
      ...(rowData ?? {}),
      repeatPattern: {
        ...(rowData?.repeatPattern ?? {}),
        until: dayjsToISOString(dayjsValue),
      },
    };
    setRowData(addRow);
    handleClosureMsgRow(addRow, true);
  };

  const handleEndByOptionsValueChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    let addRow: RowData;
    if (event.target.value === "endByEndDate") {
      addRow = {
        ...(rowData ?? {}),
        isReccuringOption2Selected: false,
        repeatPattern: {
          ...(rowData?.repeatPattern ?? {}),
          until: dayjsToISOString(endByEndDate),
        },
      };
    } else {
      addRow = {
        ...(rowData ?? {}),
        isReccuringOption2Selected: true,
        repeatPattern: {
          ...(rowData?.repeatPattern ?? {}),
        },
      };
    }
    setEndByOption(event.target.value);
    setRowData(addRow);
    handleClosureMsgRow(addRow, true);
  };

  const handleSubTabChange = (newValue: string) => {
    setLanguageTabId(newValue);
  };

  const handleLanguageMediaFile = (value: string) => {
    const messageLanguageTabId = `message_${languageTabId}`;
    let updateLanguageMessage = { ...languageMessage };
    let addRow: RowData = { ...(rowData ?? {}) };
    if (value) {
      const mediaItems = getMediaItems();
      const mediaInfo = mediaItems.find((m) => m.filePath === value);
      const newLanguageMessage: LanguageMessage = {
        [`${messageLanguageTabId}`]: {
          type: "AUDIO",
          value: value,
          accessType: (mediaInfo?.type as string) ?? "",
        },
      };
      updateLanguageMessage = {
        ...updateLanguageMessage,
        ...newLanguageMessage,
      };
      addRow = {
        ...(rowData ?? {}),
        ...updateLanguageMessage,
        languageMessage: { ...updateLanguageMessage },
      };
    } else {
      if (updateLanguageMessage[messageLanguageTabId]) {
        delete updateLanguageMessage[messageLanguageTabId];
      }
      addRow = {
        ...(rowData ?? {}),
        ...updateLanguageMessage,
        languageMessage: { ...updateLanguageMessage },
      };
      if (addRow[messageLanguageTabId]) {
        delete addRow[messageLanguageTabId];
      }
    }
    setLanguageMessage(updateLanguageMessage);
    setRowData(addRow);
    handleClosureMsgRow(addRow, true);
  };

  const subTabLanguages = useMemo(
    () => languages.map((l) => ({ name: l.name, value: l.code })),
    [languages],
  );

  return (
    <Box>
      <Box
        component="form"
        className={classes.formControl}
        noValidate
        autoComplete="off"
      >
        <Box>
          <SbuxSelect
            label={<SbuxTranslate>{"Business"}</SbuxTranslate>}
            menuItems={business}
            defaultValue={businessId}
            value={businessId}
            handleSelectChange={(value: string) => handleBusinessChange(value)}
          />
        </Box>

        <Box>
          <SbuxSelect
            label={<SbuxTranslate>{"Category"}</SbuxTranslate>}
            menuItems={globalConfigUi.closure.category}
            defaultValue={categoryId}
            value={categoryId}
            handleSelectChange={(value: string) => handleCategoryChange(value)}
            required
            autoFocus
          />
        </Box>

        <Box>
          <SbuxTextField
            label={<SbuxTranslate>{"Description"}</SbuxTranslate>}
            placeholder={descriptionPlaceHolder}
            handleChange={handleDescriptionChange}
            value={description}
            name={"description"}
            isError={descriptionError}
            required
            preventAutoFocus
            errorMessage={
              descriptionError ? (
                <SbuxTranslate>
                  {"Description required at least 1 character"}
                </SbuxTranslate>
              ) : (
                " "
              )
            }
          />
        </Box>

        <Box>
          <SbuxSelect
            label={<SbuxTranslate>{"Series Settings"}</SbuxTranslate>}
            menuItems={globalConfigUi.closure.series}
            defaultValue={seriesSettingsType}
            value={seriesSettingsType}
            handleSelectChange={(value: string) => handleSeriesChange(value)}
          />
        </Box>

        <Divider className={classes.topDiv} />

        <Box className="fullWidth">
          <FormControl>
            <InputLabel>
              <SbuxTranslate>{"Start Date and Time"}</SbuxTranslate>
            </InputLabel>

            <Box>
              <ClosureMsgTimePicker2
                type={1}
                startDate={startDate}
                endDate={endDate}
                startTimeValue={startTime}
                endTimeValue={endTime}
                timeZoneValue={startTimeZone}
                timeZoneList={globalConfigUi.timezones}
                handleDateChange={handleDateChange}
                handleStartTimeChange={handleStartTimeChange}
                handleTimeZoneChange={handleTimeZoneChange}
                row={rowData}
              />
            </Box>
          </FormControl>
        </Box>

        <Box className="fullWidth">
          <FormControl>
            <InputLabel>
              <SbuxTranslate>{"End Date and Time"}</SbuxTranslate>
            </InputLabel>

            <Box>
              <ClosureMsgTimePicker2
                type={2}
                startDate={startDate}
                endDate={endDate}
                startTimeValue={startTime}
                endTimeValue={endTime}
                timeZoneValue={endTimeZone}
                timeZoneList={globalConfigUi.timezones}
                handleDateChange={handleDateChange}
                handleEndTimeChange={handleEndTimeChange}
                handleTimeZoneChange={handleTimeZoneChange}
                row={rowData}
              />
            </Box>
          </FormControl>
        </Box>

        {seriesSettingsType === "Repeat" && (
          <>
            <Box className="fullWidth">
              <FormControl>
                <InputLabel>
                  <SbuxTranslate>{"Repeat Pattern"}</SbuxTranslate>
                </InputLabel>

                <Box className={classes.repeatPatternContainer}>
                  <Box sx={{ width: 100 }}>
                    <SbuxRadioGroup
                      radioButtonList={CM_RECURRENCE_FREQUENCY_LIST}
                      value={recPatternOption}
                      handleRadioButtonChange={handleRecPatternOptioneChange}
                    />
                  </Box>

                  <Box sx={{ width: "1px" }}>
                    <Divider
                      orientation="vertical"
                      sx={{ backgroundColor: "#006241" }}
                    />
                  </Box>
                  <Box sx={{ width: "100px" }}>
                    {recPatternOption === "DAILY" && (
                      <ClosureMsgDailyFrequency
                        dailyOption={dailyOption}
                        handleDailyOptionsValueChange={
                          handleDailyOptionsValueChange
                        }
                        dailyOptoin1InputValue={dailyOptoin1InputValue}
                        handleDailyOptoin1InputValue={
                          handleDailyOptoin1InputValue
                        }
                      />
                    )}

                    {recPatternOption === "WEEKLY" && (
                      <ClosureMsgWeeklyFrequency
                        weeklyRecDays={weeklyRecDaysChecked}
                        weeklyRecEveryInputValue={weeklyRecEveryInputValue}
                        handleCheckboxChange={handleCheckboxChange}
                        handleWeeklyRecEveryInputValueChange={
                          handleWeeklyRecEveryInputValueChange
                        }
                      />
                    )}

                    {recPatternOption === "MONTHLY" && (
                      <ClosureMsgMonthlyFrequency
                        monthlyOption={monthlyOption}
                        handleMonthlyOptionsValueChange={
                          handleMonthlyOptionsValueChange
                        }
                        monthlyRecOption1EveryDayInputValue={
                          monthlyRecOption1EveryDayInputValue
                        }
                        monthlyRecOption1EveryMonthsInputValue={
                          monthlyRecOption1EveryMonthsInputValue
                        }
                        monthlyRecOption2EveryMonthsInputValue={
                          monthlyRecOption2EveryMonthsInputValue
                        }
                        monthlyRecDayOfList={globalConfigUi.closure.monthly}
                        monthlyRecDayOfValue={monthlyRecDayOfValue}
                        handleMonthlyRecDayOfValueChange={
                          handleMonthlyRecDayOfValueChange
                        }
                        monthlyRecWeekDaysList={globalConfigUi.weekdays}
                        monthlyRecWeekDays={monthlyRecWeekDays}
                        handleMonthlyRecWeekDaysChange={
                          handleMonthlyRecWeekDaysChange
                        }
                        handleMonthlyRecOption1EveryDayInputValueChange={
                          handleMonthlyRecOption1EveryDayInputValueChange
                        }
                        handleMonthlyRecOption1EveryMonthsInputValueChange={
                          handleMonthlyRecOption1EveryMonthsInputValueChange
                        }
                        handleMonthlyRecOption2EveryMonthsInputValueChange={
                          handleMonthlyRecOption2EveryMonthsInputValueChange
                        }
                      />
                    )}
                    {recPatternOption === "YEARLY" && (
                      <ClosureMsgYearlyFrequency
                        yearlyOption={yearlyOption}
                        yearlyMonthList={globalConfigUi.months}
                        yearlyRecWeekDaysList={globalConfigUi.closure.monthly}
                        yearlyWeekDaysList={globalConfigUi.weekdays}
                        handleYearlyOptionsValueChange={
                          handleYearlyOptionsValueChange
                        }
                        yearlyRecEveryInputValue={yearlyRecEveryInputValue}
                        handleYearlyRecEveryInputValueChange={
                          handleYearlyRecEveryInputValueChange
                        }
                        yearlyOption1MonthValue={yearlyOption1MonthValue}
                        handleYearlyOption1MonthValueChange={
                          handleYearlyOption1MonthValueChange
                        }
                        yearlyOption1OnInputValue={yearlyOption1OnInputValue}
                        handleYearlyOption1OnInputValueChange={
                          handleYearlyOption1OnInputValueChange
                        }
                        yearlyOption2MonthValue={yearlyOption2MonthValue}
                        handleYearlyOption2MonthValueChange={
                          handleYearlyOption2MonthValueChange
                        }
                        yearlyRecDayOfValue={yearlyRecDayOfValue}
                        handleYearlyRecDayOfValueChange={
                          handleYearlyRecDayOfValueChange
                        }
                        yearlyRecWeekDays={yearlyRecWeekDays}
                        handleYearlyRecWeekDaysChange={
                          handleYearlyRecWeekDaysChange
                        }
                      />
                    )}
                  </Box>
                </Box>
              </FormControl>
            </Box>

            <Box className="fullWidth">
              <FormControl>
                <InputLabel>
                  <SbuxTranslate>{"End by"}</SbuxTranslate>
                </InputLabel>

                <Box>
                  <ClosureMsgRangeRecurrence
                    endByOption={endByOption}
                    endByEndDate={endByEndDate}
                    isAdd={true}
                    handleEndByEndDateChange={handleEndByEndDateChange}
                    handleEndByOptionsValueChange={
                      handleEndByOptionsValueChange
                    }
                  />
                </Box>
              </FormControl>
            </Box>
          </>
        )}

        <Divider />

        <Box className="fullWidth">
          <FormControl>
            <InputLabel>
              <SbuxTranslate>{"Audio Configuration"}</SbuxTranslate>
            </InputLabel>

            <Box>
              <SbuxSubTabs
                tabList={subTabLanguages}
                tabId={languageTabId}
                handleTabClick={(tab: { value: string }) =>
                  handleSubTabChange(tab.value)
                }
                width={"100%"}
              />
              <Box className={classes.tabContainer}>
                <ClosureAudioConfig
                  languageId={languageTabId}
                  handleLanguageMediaFile={handleLanguageMediaFile}
                  row={rowData}
                />
              </Box>
            </Box>
          </FormControl>
        </Box>
      </Box>
    </Box>
  );
};

export default ClosureMsgAdd;
